import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import JSZip from 'jszip';
import { getAllMetricsChartData } from "../../../../service/apiService";
import { Download, RotateCw } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Define types for chart data
interface ChartData {
  success: boolean;
  data: {
    months: string[];
    values: number[] | string[];
    label: string;
    metric_type: string;
  };
  statistics: {
    total: number | string;
    average: number | string;
    maximum: number | string;
    minimum: number | string;
    period: string;
  };
  chart_config: {
    chart_type: string;
    color: string;
    background_color: string;
  };
}

interface ChartProps {
  title: string;
  chartData: ChartData | null;
  isLoading: boolean;
  error: string | null;
  chartType: 'line' | 'bar';
}

export interface ChartComponentHandle {
  getPNGBlob: () => Promise<{ name: string; blob: Blob } | null>;
}

const slugifyTitle = (title: string) =>
  title.replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '');

const renderChartToCanvas = (sourceCanvas: HTMLCanvasElement): HTMLCanvasElement | null => {
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = sourceCanvas.width;
  tempCanvas.height = sourceCanvas.height;
  const ctx = tempCanvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
  ctx.drawImage(sourceCanvas, 0, 0);
  return tempCanvas;
};

const ChartComponent = forwardRef<ChartComponentHandle, ChartProps>(({ title, chartData, isLoading, error, chartType }, ref) => {
  const chartRef = useRef<any>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useImperativeHandle(ref, () => ({
    getPNGBlob: () => new Promise((resolve) => {
      const chart = chartRef.current;
      if (!chart) {
        resolve(null);
        return;
      }
      const tempCanvas = renderChartToCanvas(chart.canvas as HTMLCanvasElement);
      if (!tempCanvas) {
        resolve(null);
        return;
      }
      tempCanvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve({ name: `${slugifyTitle(title)}.png`, blob });
      }, 'image/png', 1);
    }),
  }), [title]);

  const handleDownloadPNG = () => {
    const chart = chartRef.current;
    if (!chart) return;
    setIsDownloading(true);
    try {
      const tempCanvas = renderChartToCanvas(chart.canvas as HTMLCanvasElement);
      if (!tempCanvas) return;
      const url = tempCanvas.toDataURL('image/png', 1);
      const fileName = `${slugifyTitle(title)}.png`;
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading chart as PNG:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RotateCw className="animate-spin h-5 w-5 text-violet-500 mr-2" />
        <span className="text-violet-500">Loading chart data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg p-4">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg p-4">
        <p className="text-gray-500">No chart data available</p>
      </div>
    );
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 14,
          weight: 'bold' as const
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let value = context.parsed.y;
            if (typeof value === 'string') {
              value = parseInt(value);
            }
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            if (typeof value === 'number') {
              return value.toLocaleString();
            }
            return value;
          }
        }
      }
    },
  };

  // Ensure values are numbers
  const values = chartData.data.values.map(v => typeof v === 'string' ? parseInt(v) : v);
  
  const data = {
    labels: chartData.data.months.map(month => {
      // Format the month for display (YYYY-MM to Month YYYY)
      const [year, monthNum] = month.split('-');
      const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      return date.toLocaleDateString('default', { month: 'short', year: 'numeric' });
    }),
    datasets: [
      {
        label: chartData.data.label,
        data: values,
        borderColor: chartData.chart_config.color || (chartType === 'line' ? '#7c3aed' : '#7c3aed'),
        backgroundColor: chartData.chart_config.background_color || (chartType === 'line' ? 'rgba(124, 58, 237, 0.1)' : 'rgba(124, 58, 237, 0.6)'),
        tension: 0.3,
        fill: chartType === 'line',
        borderWidth: chartType === 'bar' ? 1 : 2,
      },
    ],
  };

  const total = typeof chartData.statistics.total === 'string' 
    ? parseInt(chartData.statistics.total) 
    : chartData.statistics.total;
    
  const average = typeof chartData.statistics.average === 'string'
    ? parseFloat(chartData.statistics.average)
    : chartData.statistics.average;

  return (
    <div className="bg-white rounded-lg shadow p-4 relative">
      <button
        type="button"
        onClick={handleDownloadPNG}
        disabled={isDownloading}
        title="Download PNG"
        aria-label="Download PNG"
        className="absolute top-2 right-2 z-10 inline-flex justify-center items-center p-1.5 border border-transparent shadow-sm rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:bg-violet-400 disabled:cursor-not-allowed"
      >
        {isDownloading ? (
          <RotateCw className="animate-spin h-3.5 w-3.5" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
      </button>
      <div className="chart-wrapper h-64">
        {chartType === 'line' ?
          <Line ref={chartRef} options={chartOptions} data={data} /> :
          <Bar ref={chartRef} options={chartOptions} data={data} />
        }
      </div>
      
      <div className="flex justify-around mt-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <span className="block text-sm text-violet-600 font-medium">Total</span>
          <span className="text-lg font-bold text-violet-800">{total.toLocaleString()}</span>
        </div>
        <div className="text-center">
          <span className="block text-sm text-violet-600 font-medium">Average/Month</span>
          <span className="text-lg font-bold text-violet-800">{average.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
});

ChartComponent.displayName = 'ChartComponent';

export interface MonthlyTokenUsageChartsHandle {
  downloadAllAsZip: (month?: string) => Promise<void>;
}

const MonthlyTokenUsageCharts = forwardRef<MonthlyTokenUsageChartsHandle>((_props, ref) => {
  const [inputTokensData, setInputTokensData] = useState<ChartData | null>(null);
  const [outputTokensData, setOutputTokensData] = useState<ChartData | null>(null);
  const [sessionFilesData, setSessionFilesData] = useState<ChartData | null>(null);
  const [brainbankFilesData, setBrainbankFilesData] = useState<ChartData | null>(null);
  const [sessionsData, setSessionsData] = useState<ChartData | null>(null);
  
  const [loading, setLoading] = useState({
    inputTokens: true,
    outputTokens: true,
    sessionFiles: true,
    brainbankFiles: true,
    sessions: true,
  });
  
  const [error, setError] = useState({
    inputTokens: null as string | null,
    outputTokens: null as string | null,
    sessionFiles: null as string | null,
    brainbankFiles: null as string | null,
    sessions: null as string | null,

  });

  const inputTokensChartRef = useRef<ChartComponentHandle>(null);
  const outputTokensChartRef = useRef<ChartComponentHandle>(null);
  const sessionFilesChartRef = useRef<ChartComponentHandle>(null);
  const brainbankFilesChartRef = useRef<ChartComponentHandle>(null);
  const sessionsChartRef = useRef<ChartComponentHandle>(null);

  useImperativeHandle(ref, () => ({
    downloadAllAsZip: async (month?: string) => {
      const chartRefs = [
        inputTokensChartRef,
        outputTokensChartRef,
        sessionFilesChartRef,
        brainbankFilesChartRef,
        sessionsChartRef,
      ];
      const entries = await Promise.all(chartRefs.map((r) => r.current?.getPNGBlob() ?? null));
      const zip = new JSZip();
      let added = 0;
      for (const entry of entries) {
        if (entry) {
          zip.file(entry.name, entry.blob);
          added += 1;
        }
      }
      if (added === 0) return;
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipName = `${month ?? 'token-usage'}-token-usage-charts.zip`;
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  }));

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const result = await getAllMetricsChartData();
        setInputTokensData(result.data.input_tokens);
        setOutputTokensData(result.data.output_tokens);
        setSessionFilesData(result.data.session_files);
        setBrainbankFilesData(result.data.brainbank_files);
        setSessionsData(result.data.sessions);
        setError({
          inputTokens: null,
          outputTokens: null,
          sessionFiles: null,
          brainbankFiles: null,
          sessions: null,
        });
      } catch (err) {
        console.error('Error fetching chart data:', err);
        const message = 'Failed to load chart data';
        setError({
          inputTokens: message,
          outputTokens: message,
          sessionFiles: message,
          brainbankFiles: message,
          sessions: message,
        });
      } finally {
        setLoading({
          inputTokens: false,
          outputTokens: false,
          sessionFiles: false,
          brainbankFiles: false,
          sessions: false,
        });
      }
    };

    fetchChartData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartComponent
          ref={inputTokensChartRef}
          title="Input Tokens (Last 6 Months)"
          chartData={inputTokensData}
          isLoading={loading.inputTokens}
          error={error.inputTokens}
          chartType="line"
        />
        <ChartComponent
          ref={outputTokensChartRef}
          title="Output Tokens (Last 6 Months)"
          chartData={outputTokensData}
          isLoading={loading.outputTokens}
          error={error.outputTokens}
          chartType="line"
        />
        <ChartComponent
          ref={sessionFilesChartRef}
          title="Files Uploaded in Session (Last 6 Months)"
          chartData={sessionFilesData}
          isLoading={loading.sessionFiles}
          error={error.sessionFiles}
          chartType="bar"
        />
        <ChartComponent
          ref={brainbankFilesChartRef}
          title="Files Uploaded in BrainBanks (Last 6 Months)"
          chartData={brainbankFilesData}
          isLoading={loading.brainbankFiles}
          error={error.brainbankFiles}
          chartType="bar"
        />
        <ChartComponent
          ref={sessionsChartRef}
          title="Sessions Created (Last 6 Months)"
          chartData={sessionsData}
          isLoading={loading.sessions}
          error={error.sessions}
          chartType="bar"
        />
      </div>
    </div>
  );
});

MonthlyTokenUsageCharts.displayName = 'MonthlyTokenUsageCharts';

export default MonthlyTokenUsageCharts;